import { Link } from "react-router-dom";
import { motion, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { BallHalf } from "./Ball";
import { Emblem } from "./Emblem";

export const T = { introOut: [0.03, 0.14], fly: [0.12, 0.42], hit: 0.42, cards: 0.48, settle: [0.66, 0.76], reveal: [0.66, 0.78] };
const rnd = (i) => { const x = Math.sin(i * 127.1 + 311.7) * 43758.5453; return x - Math.floor(x); };
const clamp = (v) => Math.min(1, Math.max(0, v));
const flyT = (v) => clamp((v - T.fly[0]) / (T.fly[1] - T.fly[0]));
const pos = (v) => {
  const t = flyT(v), e = 1 - (1 - t) * (1 - t);
  const W = window.innerWidth, H = window.innerHeight;
  return { x: (1 - e) * W * 0.7, y: -(1 - e) * H * 0.18 - Math.sin(Math.PI * t) * H * 0.05, t };
};

export const IntroLayer = ({ p }) => {
  const { t } = useLang();
  const opacity = useTransform(p, T.introOut, [1, 0]);
  const y = useTransform(p, T.introOut, [0, -40]);
  const filter = useTransform(p, T.introOut, ["blur(0px)", "blur(8px)"]);
  return (
    <motion.div style={{ opacity, y, filter }} className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 pointer-events-none" data-testid="hero-intro">
      <span className="hero-spark mb-8" data-testid="hero-spark" />
      <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.05] max-w-4xl" data-testid="hero-intro-headline">{t("hero_intro")}</h2>
      <div className="absolute bottom-10 flex flex-col items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-[#C9B6F2]/70" data-testid="hero-scroll-indicator">
        <span className="h-10 w-6 rounded-full border border-[#C9B6F2]/40 grid justify-center pt-2"><span className="scroll-dot h-2 w-1 rounded-full bg-[#F2C230]" /></span>
        {t("hero_scroll")}
      </div>
    </motion.div>
  );
};

const Ghost = ({ p, k }) => {
  const x = useTransform(p, (v) => pos(v - k * 0.011).x);
  const y = useTransform(p, (v) => pos(v - k * 0.011).y);
  const opacity = useTransform(p, (v) => { const t = flyT(v); return t > 0 && t < 1 ? (0.55 - k * 0.07) * Math.min(1, t * 6) : 0; });
  const size = 1 - k * 0.09;
  return (
    <motion.div style={{ x, y, opacity, scale: size }} aria-hidden
      className={`absolute left-1/2 top-1/2 -ml-[60px] -mt-[60px] sm:-ml-[90px] sm:-mt-[90px] h-[120px] w-[120px] sm:h-[180px] sm:w-[180px] rounded-full blur-xl ${k % 2 ? "bg-[#8A5CD6]" : "bg-[#F2C230]"}`} />
  );
};

export const FlyingBall = ({ p }) => {
  const x = useTransform(p, (v) => pos(v).x);
  const y = useTransform(p, (v) => pos(v).y);
  const rotate = useTransform(p, (v) => -flyT(v) * 720);
  const scale = useTransform(p, [T.fly[0], T.hit - 0.02, T.hit, T.hit + 0.02], [0.45, 1, 1.12, 1]);
  const visible = useTransform(p, [T.fly[0], T.fly[0] + 0.02, T.hit + 0.1, T.hit + 0.14], [0, 1, 1, 0]);
  const split = useTransform(p, [T.hit + 0.02, T.hit + 0.14], [0, 1], { clamp: true });
  const topY = useTransform(split, [0, 1], [0, -170]), topX = useTransform(split, [0, 1], [0, -70]), topR = useTransform(split, [0, 1], [0, -28]);
  const botY = useTransform(split, [0, 1], [0, 170]), botX = useTransform(split, [0, 1], [0, 70]), botR = useTransform(split, [0, 1], [0, 22]);
  const crack = useTransform(p, [T.hit - 0.005, T.hit + 0.01, T.hit + 0.03], [0, 1, 0]);
  const holo = useTransform(p, [T.fly[0], T.fly[0] + 0.03, T.hit - 0.01, T.hit], [0, 1, 1, 0]);
  return (
    <div className="absolute inset-0 pointer-events-none" data-testid="hero-ball-layer">
      {[6, 5, 4, 3, 2, 1].map((k) => <Ghost key={k} p={p} k={k} />)}
      <motion.div style={{ x, y, opacity: visible }} className="absolute left-1/2 top-1/2 -ml-[70px] -mt-[70px] sm:-ml-[100px] sm:-mt-[100px] w-[140px] sm:w-[200px]" data-testid="hero-ball">
        <motion.div style={{ rotate, scale }} className="relative drop-shadow-[0_0_30px_rgba(242,194,48,.45)]">
          <motion.div style={{ x: topX, y: topY, rotate: topR }}><BallHalf top /></motion.div>
          <motion.div style={{ x: botX, y: botY, rotate: botR }} className="-mt-[3px]"><BallHalf /></motion.div>
          <motion.div style={{ opacity: holo }} className="ball-holo absolute inset-[2%] rounded-full pointer-events-none" data-testid="hero-ball-holo" />
          <motion.svg style={{ opacity: crack }} viewBox="0 0 200 200" className="absolute inset-0 h-full w-full">
            <path d="M100 8 L88 52 L112 74 L92 104 L118 134 L96 160 L106 196" fill="none" stroke="#FFF6C9" strokeWidth="4" strokeLinejoin="round" />
            <path d="M92 104 L52 92 M118 134 L156 150 M112 74 L150 56" fill="none" stroke="#FFF6C9" strokeWidth="2.5" />
          </motion.svg>
        </motion.div>
      </motion.div>
    </div>
  );
};

const Particle = ({ p, i }) => {
  const a = (i / 30) * Math.PI * 2 + rnd(i) * 0.4, d = 140 + rnd(i + 9) * 320, s = 4 + rnd(i + 4) * 7;
  const q = useTransform(p, [T.hit, T.hit + 0.16 + rnd(i + 2) * 0.06], [0, 1], { clamp: true });
  const x = useTransform(q, (v) => Math.cos(a) * d * (1 - (1 - v) ** 3));
  const y = useTransform(q, (v) => Math.sin(a) * d * (1 - (1 - v) ** 3) + v * v * 70);
  const opacity = useTransform(q, [0, 0.08, 0.7, 1], [0, 1, 0.8, 0]);
  return <motion.span style={{ x, y, opacity, width: s, height: s }} className={`absolute left-1/2 top-1/2 rounded-full ${i % 3 ? "bg-[#B98CFF] shadow-[0_0_10px_#8A5CD6]" : "bg-[#F2C230] shadow-[0_0_12px_#F2C230]"}`} />;
};

export const BreakBurst = ({ p }) => {
  const flash = useTransform(p, [T.hit - 0.005, T.hit + 0.01, T.hit + 0.1], [0, 0.9, 0]);
  const ringScale = useTransform(p, [T.hit, T.hit + 0.12], [0.3, 4]);
  const ringOpacity = useTransform(p, [T.hit, T.hit + 0.01, T.hit + 0.12], [0, 0.9, 0]);
  return (
    <div className="absolute inset-0 pointer-events-none z-20" data-testid="hero-burst">
      <motion.div style={{ opacity: flash }} className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,#FFF6C9_0%,rgba(242,194,48,.6)_22%,rgba(138,92,214,.35)_55%,transparent_80%)]" data-testid="impact-flash" />
      <motion.div style={{ scale: ringScale, opacity: ringOpacity }} className="absolute left-1/2 top-1/2 -ml-[100px] -mt-[100px] h-[200px] w-[200px] rounded-full border-4 border-[#F2C230] shadow-[0_0_40px_#8A5CD6]" data-testid="impact-ring" />
      {Array.from({ length: 30 }, (_, i) => <Particle key={i} p={p} i={i} />)}
    </div>
  );
};

export const RevealContent = ({ p }) => {
  const { t } = useLang();
  const opacity = useTransform(p, T.reveal, [0, 1]);
  const y = useTransform(p, T.reveal, [50, 0]);
  const scale = useTransform(p, T.reveal, [0.96, 1]);
  const pointerEvents = useTransform(p, (v) => (v > T.reveal[0] + 0.04 ? "auto" : "none"));
  const [a, b] = t("tagline").split(". ");
  return (
    <motion.div style={{ opacity, y, scale, pointerEvents }} className="relative z-30 max-w-3xl mx-auto px-4 text-center space-y-6 lg:space-y-8" data-testid="hero-text">
      <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#C9B6F2] border border-[#F2C230]/30 rounded-full px-4 py-1.5"><ShieldCheck className="h-3.5 w-3.5" />{t("hero_badge")}</span>
      <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02]">{a}.<br /><em className="text-gold-grad not-italic">{b}</em></h1>
      <p className="text-white/60 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">{t("hero_sub")}</p>
      <div className="flex flex-wrap justify-center gap-4">
        <Link to="/shop" data-testid="hero-shop-btn" className="btn-gold">{t("hero_cta")} <ArrowRight className="h-4 w-4" /></Link>
        <Link to="/about" data-testid="hero-story-btn" className="btn-ghost">{t("hero_cta2")}</Link>
      </div>
    </motion.div>
  );
};

const FAN = [{ x: -170, y: 10, r: -22 }, { x: -86, y: -30, r: -11 }, { x: 0, y: -48, r: 0 }, { x: 86, y: -30, r: 11 }, { x: 170, y: 10, r: 22 }];

const CardBack = () => (
  <div className="relative h-full w-full rounded-xl overflow-hidden bg-[#1B1430] border-[3px] border-[#F2C230] shadow-[0_20px_40px_-10px_rgba(138,92,214,.6)]">
    <div className="absolute inset-1.5 rounded-lg border border-[#C9B6F2]/30 bg-[radial-gradient(circle_at_50%_45%,#6E3FB8_0%,#241A3A_55%,#120E1F_100%)]" />
    <div className="absolute inset-0 grid place-items-center"><Emblem className="h-14 w-14" /></div>
    <div className="pack-shine absolute inset-0" />
  </div>
);

const PackCard = ({ p, i }) => {
  const start = T.cards + i * 0.022;
  const cp = useTransform(p, [start, start + 0.08], [0, 1], { clamp: true });
  const f = FAN[i];
  const x = useTransform(cp, [0, 1], [0, f.x]);
  const y = useTransform(cp, [0, 1], [30, f.y]);
  const rotate = useTransform(cp, [0, 1], [0, f.r]);
  const scale = useTransform(cp, [0, 1], [0.2, 1]);
  const opacity = useTransform(cp, [0, 0.2, 1], [0, 1, 1]);
  return (
    <motion.div style={{ x, y, rotate, scale, opacity, zIndex: 10 - Math.abs(i - 2) }}
      className="absolute left-1/2 top-1/2 -ml-[60px] -mt-[84px] h-[168px] w-[120px]" data-testid={`pack-card-${i}`}>
      <CardBack />
    </motion.div>
  );
};

export const CardFan = ({ p }) => {
  const opacity = useTransform(p, T.settle, [1, 0.16]);
  const scale = useTransform(p, T.settle, [1, 1.15]);
  const filter = useTransform(p, T.settle, ["blur(0px)", "blur(2px)"]);
  return (
    <motion.div style={{ opacity, scale, filter }} className="absolute inset-0 pointer-events-none z-10" data-testid="hero-card-fan">
      <div className="absolute inset-0 max-sm:scale-[0.7]">{FAN.map((f, i) => <PackCard key={f.x} p={p} i={i} />)}</div>
    </motion.div>
  );
};
